import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "../../contexts/ApiClientContext";
import { useAuth } from "../../contexts/AuthContext";
import { useAlerts } from "../../contexts/AlertsContext";
import APIEndPoints from "../../utils/APIEndPoints";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import { IApiClientError } from "../../shared/ApiClient";
import APIError from "../../utils/APIError";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import Loading from "../common/Loading";
import FormContainer from "../common/FormContainer";
import { FaInfoCircle } from "react-icons/fa";
import FormInputText from "../common/FormInputText";
import Site from "../../model/Site";
import ClientSSOMetadata from "../../model/ClientSSOMetadata";
import ClientSSO from "../../model/ClientSSO";

interface IClientsSSOFormErrors {
  client_name: string;
  client_uri: string;
  redirect_uris: string;
}

const defaultFormData = new ClientSSO(
  "",
  "",
  new ClientSSOMetadata("", "", "", "", "", "", "")
);

const defaultFormErrors: IClientsSSOFormErrors = {
  client_name: "",
  client_uri: "",
  redirect_uris: "",
};

function ClientsSSOAddEdit() {
  // read the ID from the url
  const { id } = useParams();

  // manage loading animation
  const [loading, setLoading] = useState(true);
  // manage pilots
  const [sites, setSites] = useState<Site[]>([]);
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // set state for form info
  const [formData, setFormData] = useState<ClientSSO>(defaultFormData);
  // set state for form errors
  const [formErrors, setFormErrors] =
    useState<IClientsSSOFormErrors>(defaultFormErrors);

  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the alerts system
  const alertsManager = useAlerts();

  // load record data from the server
  useEffect(() => {
    const getAllData = async () => {
      try {
        let clientSSO: ClientSSO | undefined = undefined;
        if (id) {
          clientSSO = await apiClient?.get<ClientSSO>({
            url: APIEndPoints.CLIENTS.ONE(parseInt(id)),
          });
        }

        // set into the state all the information
        setFormData(clientSSO ? clientSSO : defaultFormData);
        setFormErrors(defaultFormErrors);
      } catch (error: any) {
        if (!error.processed) {
          setErrorMessage("Something went wrong. Try again later!");
        }
      }
      setLoading(false);
    };
    getAllData();
  }, [apiClient, id]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // validate errors from the form
    /* const errors = validateSiteForm(formData);

   if (isFormInvalid(errors)) {
     setFormErrors(errors);
     setErrorMessage("Some of the data input is invalid. Please check and try again.");
     return;
   } */

    var body = new ClientSSOMetadata(
      formData.client_metadata.client_name,
      formData.client_metadata.client_uri,
      "",
      formData.client_metadata.redirect_uris,
      "",
      "",
      ""
    );

    // check if we have EDIT or CREATE
    if (id) {
      /* body.id = parseInt(id);
     apiClient
       ?.put({
         url: APIEndPoints.CHELEMENTS.ONE(parseInt(id)),
         body: formData,
       })
       .then((response) => {
         setFormErrors(defaultFormErrors); // Reset errors
         alertsManager.showAlertMessage({
           type: AlertMessageType.SUCCESS,
           message: "Artefact record updated successfully",
         });
         navigate(SETTINGS.get_path({ section: AppSections.CHELEMENTS }));
       })
       .catch((error: IApiClientError) => {
         if (!error.processed) {
           processAPIErrors(error);
         }
       }); */
    } else {
      apiClient
        ?.post({
          url: APIEndPoints.CLIENTS.NEW,
          body: formData.client_metadata,
        })
        .then((response) => {
          setFormErrors(defaultFormErrors);
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "SSO Client record created successfully",
          });
          navigate(SETTINGS.get_path({ section: AppSections.CLIENTS }));
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            processAPIErrors(error);
          }
        });
    }
  };

  const handleDelete = () => {
    apiClient
      ?.delete({
        url: APIEndPoints.CLIENTS.ONE(parseInt(id!)),
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "SSO Client record deleted successfully",
        });
        navigate(SETTINGS.get_path({ section: AppSections.CLIENTS }));
      })
      .catch((error: IApiClientError) => {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Unable to delete the ClientSSO record",
          });
        }
      });
  };

  const formInputChange = (inputID: string, newValue: string) => {
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy.client_metadata[inputID] = newValue;
    setFormData(formDataCopy as ClientSSO);
  };

  const onFormCancel = () => {
    navigate(SETTINGS.go_back({ section: AppSections.CLIENTS }));
  };

  const processAPIErrors = (serverError: IApiClientError) => {
    try {
      // convert the error to ChemiNovaAPI custom errors
      const apiErrors = serverError.details.response.data as APIError[];
      const formErrorsCopy: any = defaultFormErrors;
      console.log(serverError);
      console.log(apiErrors);
      apiErrors.forEach((apiError) => {
        formErrorsCopy[apiError.loc[0]] = apiError.msg;
      });
      setFormErrors(formErrorsCopy);
      setErrorMessage(
        "Some of the data input is invalid. Please check and try again."
      );
    } catch (exception) {
      console.log(exception);
      setErrorMessage("Something went wrong. Please try again later!");
    }
  };

  const pageTitle = id ? "Edit SSO Client record" : "Create a new SSO Client";
  const confirmLabel = id ? "Update SSO Client" : "Create SSO Client";

  const deleteButton = id ? (
    <Button
      label="Delete SSO Client"
      purpose={ButtonAction.DELETE}
      onClick={() => {
        alertsManager.showDialogWindow({
          type: DialogWindowType.CONFIRM,
          message: "Are you sure you want to delete this SSO Client?",
          cancelLabel: "Cancel",
          confirmLabel: "Yes, delete SSO Client",
          onConfirm: handleDelete,
        });
      }}
    />
  ) : null;

  if (loading) {
    return <Loading />;
  }

  return (
    <FormContainer
      title={pageTitle}
      onSubmit={handleSubmit}
      headButton={deleteButton}
    >
      {/* <FormInputImage
      label="Picture"
      id="icon"
      value={formData.icon}
      required={false}
      onChange={formInputChange}
      error={formErrors.icon}
    /> */}

      <FormInputText
        label="Name"
        id="client_name"
        value={formData.client_metadata.client_name}
        required={true}
        placeholder="Name"
        onChange={formInputChange}
        //error={formErrors.name}
      />

      <FormInputText
        label="URI"
        id="client_uri"
        value={formData.client_metadata.client_uri}
        required={true}
        placeholder="URI"
        onChange={formInputChange}
        //error={formErrors.name}
      />

      <FormInputText
        label="Redirect URI"
        id="redirect_uris"
        value={formData.client_metadata.redirect_uris}
        required={true}
        placeholder="Redirect URI"
        onChange={formInputChange}
        //error={formErrors.name}
      />

      {errorMessage && (
        <div className="form-submit-error">
          <FaInfoCircle />
          <div>{errorMessage}</div>
        </div>
      )}

      <div className="form-buttons-container">
        <Button
          label="Cancel"
          purpose={ButtonAction.CANCEL}
          onClick={() => onFormCancel()}
          className="w-full"
        />
        <Button
          label={confirmLabel}
          purpose={ButtonAction.SAVE}
          isSubmit={true}
          className="w-full"
        />
      </div>
    </FormContainer>
  );
}

/* function validateSiteForm(formData: CHElement): IChElementFormErrors {
  let errors: IChElementFormErrors = {name: "", description: "", id_site: "", start_year: "", end_year: "", approximate_date: ""}

  errors.name = formData.name.length < 4 ? "Artefact name should be at least 4 characters long" : "";
  errors.description = formData.description.length < 10 ? "Artefact description should be at least 10 characters long" : "";
  errors.id_site = formData.id_site < 1 ? "Please select the site that the artefact belongs to" : "";
  errors.start_year = formData.start_year < 1000 ? "Start year should be at least 1000" : "";
  errors.end_year = formData.end_year > 2024 ? "End year should be maximum the current year" : "";
  errors.approximate_date = formData.approximate_date < -128 || formData.approximate_date > 127 ? "Approximate date should be between -128 and 127" : "";

  return errors;
}

function isFormInvalid(errors: IChElementFormErrors): boolean {
  return errors.name !== "" || errors.description !== "" || errors.id_site !== "" || errors.start_year !== "" || errors.end_year !== "" || errors.approximate_date !== "";
} */

export default ClientsSSOAddEdit;
